import { Button } from "@headlessui/react";
import LabelField from "@shared/components/LabelField";
import clsx from "clsx";
import { Eye, EyeOff } from "lucide-react";
import { useRef, useState } from "react";

/**
 * Props interface for the PasswordLabelField component.
 */
interface PasswordLabelFieldProps extends Omit<React.ComponentProps<typeof LabelField>, "type"> {}

/**
 * A password input field component with toggle visibility functionality.
 *
 * Features:
 * - Password visibility toggle with eye icon
 * - Built on top of LabelField for consistent styling
 * - Proper accessibility with ARIA labels
 * - Error state handling with visual feedback
 * - Responsive design with Tailwind CSS
 * - Enhanced focus states and visual feedback for accessibility
 * - Compatible with react-hook-form ref forwarding
 *
 * @example
 * ```tsx
 * <PasswordLabelField
 *   label="Enter Password"
 *   error="Password is required"
 *   required
 * />
 * ```
 */
export default function PasswordLabelField({
  label = "Password",
  error,
  ref,
  onChange,
  onBlur,
  className,
  ...props
}: PasswordLabelFieldProps) {
  /** Tracks whether the password is currently visible */
  const [showPassword, setShowPassword] = useState(false);

  // Internal ref for focus management
  const internalRef = useRef<HTMLElement>(null);

  /**
   * Toggles the password visibility state.
   * Switches between showing the password as hidden or plain text.
   */
  const togglePasswordVisibility = () => {
    internalRef.current?.focus();
    setShowPassword((prev) => !prev);
  };

  return (
    <div className="relative">
      <LabelField
        label={label}
        type={showPassword ? "text" : "password"}
        error={error}
        className={clsx("pr-12", className)} // Space for toggle button + custom className
        onChange={onChange}
        onBlur={onBlur}
        ref={(element) => {
          // Store in our internal ref for focus management
          internalRef.current = element;
          // Forward to the external ref
          if (typeof ref === "function") {
            ref(element);
          } else if (ref) {
            ref.current = element;
          }
        }}
        {...props}
      />
      <Button
        type="button"
        onClick={togglePasswordVisibility}
        className={clsx(
          "absolute right-3 p-2",
          "text-gray-400 hover:text-gray-600",
          "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2",
          "active:scale-95 transition-all duration-150 ease-in-out",
          "rounded-md hover:bg-gray-100 focus:bg-gray-100",
          {
            "bottom-3.5": !!error, // When error exists, position from bottom of input
            "top-1/2 -translate-y-1/2": !error, // When no error, center vertically
          }
        )}
        aria-label={showPassword ? "Hide password" : "Show password"}
        aria-pressed={showPassword}
        // testid in kebab case
        data-testid={`${props.name?.replace(/([A-Z])/g, "-$1").toLowerCase()}-toggle`}
      >
        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
      </Button>
    </div>
  );
}
